import type { Frame } from "../renderer/Frame";
import type { DPoint } from "./types";

// Candle-derived analytics used by drawing tools (regression, VWAP, volume profile, tick stats, FVG fill).

export const idxOf = (f: Frame, p: DPoint) => Math.max(0, Math.min(f.candles.length - 1, Math.round(f.fracIndexAt(f.xt(p.time)))));

export interface Regression { slope: number; intercept: number; sigma: number; r: number; i1: number; i2: number }

export function regression(f: Frame, a: DPoint, b: DPoint): Regression | null {
  const i1 = Math.min(idxOf(f, a), idxOf(f, b)), i2 = Math.max(idxOf(f, a), idxOf(f, b));
  const n = i2 - i1 + 1;
  if (n < 2) return null;
  let sx = 0, sy = 0, sxx = 0, sxy = 0, syy = 0;
  for (let i = i1; i <= i2; i++) { const x = i - i1, y = f.candles[i].close; sx += x; sy += y; sxx += x * x; sxy += x * y; syy += y * y; }
  const den = n * sxx - sx * sx || 1;
  const slope = (n * sxy - sx * sy) / den, intercept = (sy - slope * sx) / n;
  let ss = 0;
  for (let i = i1; i <= i2; i++) { const e = f.candles[i].close - (intercept + slope * (i - i1)); ss += e * e; }
  const r = (n * sxy - sx * sy) / (Math.sqrt(den * (n * syy - sy * sy)) || 1);
  return { slope, intercept, sigma: Math.sqrt(ss / n), r, i1, i2 };
}

export function anchoredVwap(f: Frame, a: DPoint): { i: number; v: number }[] {
  const i0 = idxOf(f, a), out: { i: number; v: number }[] = [];
  let pv = 0, vv = 0;
  for (let i = i0; i < f.candles.length; i++) {
    const c = f.candles[i], vol = c.volume || 1, tp = (c.high + c.low + c.close) / 3;
    pv += tp * vol; vv += vol;
    if (i >= f.start - 1 && i <= f.end + 1) out.push({ i, v: pv / vv });
  }
  return out;
}

export interface VolumeProfile { rows: { lo: number; hi: number; vol: number; up: number }[]; max: number; poc: number; i1: number; i2: number; total: number }

export function volumeProfile(f: Frame, a: DPoint, b: DPoint | null, rowsN = 24): VolumeProfile | null {
  const i1 = b ? Math.min(idxOf(f, a), idxOf(f, b)) : idxOf(f, a);
  const i2 = b ? Math.max(idxOf(f, a), idxOf(f, b)) : f.candles.length - 1;
  if (i2 < i1) return null;
  let lo = Infinity, hi = -Infinity;
  for (let i = i1; i <= i2; i++) { lo = Math.min(lo, f.candles[i].low); hi = Math.max(hi, f.candles[i].high); }
  if (!(hi > lo)) return null;
  const h = (hi - lo) / rowsN;
  const rows = Array.from({ length: rowsN }, (_, k) => ({ lo: lo + k * h, hi: lo + (k + 1) * h, vol: 0, up: 0 }));
  let total = 0;
  for (let i = i1; i <= i2; i++) {
    const c = f.candles[i], vol = c.volume || 1, span = Math.max(c.high - c.low, h / 4);
    total += vol;
    for (const r of rows) {
      const ov = Math.max(0, Math.min(c.high, r.hi) - Math.max(c.low, r.lo));
      if (ov <= 0) continue;
      const part = (vol * ov) / span;
      r.vol += part; if (c.close >= c.open) r.up += part;
    }
  }
  let max = 0, poc = 0;
  rows.forEach((r, k) => { if (r.vol > max) { max = r.vol; poc = k; } });
  return { rows, max, poc, i1, i2, total };
}

export interface TickStats { ticks: number; up: number; down: number; seconds: number; speed: number; dominance: number; imbalance: number; bars: number }

export function tickStats(f: Frame, a: DPoint, b: DPoint): TickStats {
  const i1 = Math.min(idxOf(f, a), idxOf(f, b)), i2 = Math.max(idxOf(f, a), idxOf(f, b));
  let up = 0, down = 0, ticks = 0;
  for (let i = i1; i <= i2; i++) {
    const c = f.candles[i];
    const u = c.upTicks || 0, d = c.downTicks || 0;
    if (u + d > 0) { up += u; down += d; ticks += u + d; }
    else { ticks += c.volume || 1; if (c.close >= c.open) up += c.volume || 1; else down += c.volume || 1; }
  }
  const seconds = Math.max(1, Math.abs(b.time - a.time) || (i2 - i1 + 1) * f.step);
  return { ticks, up, down, seconds, speed: ticks / seconds, dominance: ticks ? (Math.max(up, down) / ticks) * 100 : 50, imbalance: ticks ? ((up - down) / ticks) * 100 : 0, bars: i2 - i1 + 1 };
}

// How much of a price zone has been traded through AFTER its right edge (0 = untouched, 1 = fully filled).
export function zoneFill(f: Frame, top: number, bottom: number, fromIdx: number): number {
  let hi = -Infinity, lo = Infinity;
  for (let i = fromIdx + 1; i < f.candles.length; i++) { hi = Math.max(hi, f.candles[i].high); lo = Math.min(lo, f.candles[i].low); }
  if (hi === -Infinity) return 0;
  const T = Math.max(top, bottom), B = Math.min(top, bottom), span = T - B || 1;
  return Math.min(1, Math.max(0, Math.min(T, hi) - Math.max(B, lo)) / span);
}
